import { useEffect, useState } from "react";
import * as api from "../../api/client";
import type { RuntimeStats } from "../../api/client";
import { pixelTheme } from "../../theme/pixel";
import { panelStyle } from "../../theme/styles";

const POLL_MS = 5000;

export function StatsTab() {
  const theme = pixelTheme;
  const [stats, setStats] = useState<RuntimeStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      try {
        const next = await api.getRuntimeStats();
        if (cancelled) return;
        setStats(next);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      }
    };

    void tick();
    const handle = window.setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, []);

  return (
    <div
      style={{
        ...panelStyle(theme),
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        maxWidth: 520,
      }}
    >
      {error ? (
        <div style={{ color: theme.inkFaint }}>error: {error}</div>
      ) : null}
      {stats ? (
        <StatsList stats={stats} />
      ) : !error ? (
        <div style={{ color: theme.inkDim }}>loading…</div>
      ) : null}
    </div>
  );
}

function StatsList({ stats }: { stats: RuntimeStats }) {
  const theme = pixelTheme;
  const rows: Array<[string, string]> = [
    ["Online users", String(stats.online_users)],
    ["Peak online (24h)", String(stats.peak_online_24h)],
    ["Current matches", String(stats.current_matches)],
    ["Current rooms", String(stats.current_rooms)],
    ["Registered users", String(stats.registered_users)],
    ["Uptime", formatUptime(stats.uptime_seconds)],
  ];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto auto",
        rowGap: 6,
        columnGap: 24,
        fontFamily: theme.fontBody,
        fontSize: 18,
      }}
    >
      {rows.map(([label, value]) => (
        <Row key={label} label={label} value={value} />
      ))}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  const theme = pixelTheme;
  return (
    <>
      <div style={{ color: theme.inkDim, textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ color: theme.ink, textAlign: "right" }}>{value}</div>
    </>
  );
}

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  if (h < 24) return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
}
